import type { ProductType } from '@/lib/types/delivery-uploads';
export const queryKeys = {
  /** NPJB-899 — one infinite query per Home search group, keyed by the debounced term. */
  homeSearch: {
    all: ['homeSearch'] as const,
    group: (group: 'campaign' | 'audience' | 'account', q: string, withSi: boolean) =>
      [...queryKeys.homeSearch.all, group, q, withSi] as const,
  },
  /** NPJB-897 — the results strip: the tile list, then one query per tile. */
  homeResults: {
    all: ['homeResults'] as const,
    list: () => [...queryKeys.homeResults.all, 'list'] as const,
    tile: (key: 'signals' | 'accountsChangedPlay' | 'activeCampaigns' | 'leads' | 'impressions') =>
      [...queryKeys.homeResults.all, 'tile', key] as const,
  },
  homeFeed: {
    all: ['homeFeed'] as const,
    /** NPJB-950: with or without the Signal Intelligence rows the user may see. */
    list: (si: boolean) => [...queryKeys.homeFeed.all, { si }] as const,
  },
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (params: { page: number; size: number }) => [...queryKeys.users.lists(), params] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
  },
  filterOptions: {
    all: ['filterOptions'] as const,
    byType: (type: string) => [...queryKeys.filterOptions.all, type] as const,
  },
  uploads: {
    all: ['uploads'] as const,
  },
  audienceBrief: {
    all: ['audienceBrief'] as const,
  },
  audienceRequest: {
    all: ['audienceRequest'] as const,
    detail: (id: string) => [...queryKeys.audienceRequest.all, id] as const,
    prepareReview: (id: string) =>
      [...queryKeys.audienceRequest.detail(id), 'prepareReview'] as const,
    splitOptions: (id: string) =>
      [...queryKeys.audienceRequest.detail(id), 'splitOptions'] as const,
  },
  audiences: {
    all: ['audiences'] as const,
    counts: (params: { mine?: boolean; campaignId?: string; tenantId?: string } = {}) =>
      [...queryKeys.audiences.all, 'counts', params] as const,
    sections: () => [...queryKeys.audiences.all, 'section'] as const,
    section: (params: {
      group?: 'priority' | 'other';
      status?: string;
      search?: string;
      campaignId?: string;
      mine?: boolean;
      tenantId?: string;
    }) => [...queryKeys.audiences.sections(), params] as const,
  },
  /** Saved ICP audiences — the switcher's list (NPJB-711). */
  savedAudiences: {
    all: ['savedAudiences'] as const,
    list: () => [...queryKeys.savedAudiences.all, 'list'] as const,
    /** The Intelligence audience picker (NPJB-586): one list per client read. */
    pickers: () => [...queryKeys.savedAudiences.all, 'picker'] as const,
    picker: (tenantId: string | undefined) =>
      [...queryKeys.savedAudiences.pickers(), tenantId ?? 'own'] as const,
  },
  audienceOverview: {
    all: ['audienceOverview'] as const,
    detail: (id: string) => [...queryKeys.audienceOverview.all, id] as const,
  },
  pushRfp: {
    all: ['pushRfp'] as const,
    status: (id: string) => [...queryKeys.pushRfp.all, id] as const,
    /** Opportunity + Account resolved from an IO number — keyed on the number itself. */
    opportunityByIo: (ioNumber: string) =>
      [...queryKeys.pushRfp.all, 'opportunityByIo', ioNumber] as const,
    /** Org-wide, not per-audience — the picklist is a property of the Salesforce field. */
    requestForOptions: () => [...queryKeys.pushRfp.all, 'requestForOptions'] as const,
  },
  pushOli: {
    all: ['pushOli'] as const,
    status: (id: string) => [...queryKeys.pushOli.all, 'status', id] as const,
    /** The modal's pricing table — one row per selected sub-product. */
    pricingRows: (id: string) => [...queryKeys.pushOli.all, 'pricingRows', id] as const,
    /** The description built from the audience, filled in on open (NPJB-846). */
    generatedDescription: (id: string) =>
      [...queryKeys.pushOli.all, 'generatedDescription', id] as const,
  },
  /** Campaign touchpoint materials on the audience overview Materials tab (NPJB-549). */
  audienceMaterials: {
    all: ['audienceMaterials'] as const,
    list: (requestId: string) => [...queryKeys.audienceMaterials.all, requestId] as const,
  },
  deliveryUploads: {
    all: ['deliveryUploads'] as const,
    list: (requestId: string, productType: ProductType) =>
      [...queryKeys.deliveryUploads.all, requestId, productType] as const,
    metrics: (requestId: string, productType: ProductType) =>
      [...queryKeys.deliveryUploads.all, requestId, productType, 'metrics'] as const,
  },
  /**
   * DSP impressions chart data — served by `GET /audience/requests/{id}/delivery-metrics`
   * WITHOUT a productType query param. This is a separate endpoint from the unified
   * delivery rollup (`deliveryUploads.metrics`) which uses the SAME path but is
   * discriminated by `?productType=...`. The DSP route is named historically.
   */
  impressionsMetrics: {
    all: ['impressionsMetrics'] as const,
    detail: (id: string) => [...queryKeys.impressionsMetrics.all, id] as const,
  },
  geo: {
    all: ['geo'] as const,
    regions: (includeSubregions: boolean) =>
      [...queryKeys.geo.all, 'regions', includeSubregions] as const,
    countries: () => [...queryKeys.geo.all, 'countries'] as const,
  },
  intentTopics: {
    all: ['intentTopics'] as const,
    index: () => [...queryKeys.intentTopics.all, 'index'] as const,
    detail: (id: string) => [...queryKeys.intentTopics.all, 'detail', id] as const,
    browse: (category: string, subcategory: string) =>
      [...queryKeys.intentTopics.all, 'browse', category, subcategory] as const,
    // NPJB-625 — requested names still pending, shown greyed in topic search.
    requests: () => [...queryKeys.intentTopics.all, 'requests'] as const,
  },
  technographics: {
    all: ['technographics'] as const,
    // The whole tree, products included: one request, cached for the session.
    tree: () => [...queryKeys.technographics.all, 'tree'] as const,
  },
  products: {
    all: ['products'] as const,
    list: () => [...queryKeys.products.all, 'list'] as const,
  },
  campaigns: {
    all: ['campaigns'] as const,
    list: () => [...queryKeys.campaigns.all, 'list'] as const,
    counts: (params: { mine?: boolean; tenantId?: string } = {}) =>
      [...queryKeys.campaigns.all, 'counts', params] as const,
    sections: () => [...queryKeys.campaigns.all, 'section'] as const,
    section: (params: {
      group?: 'active' | 'other';
      status?: string;
      search?: string;
      mine?: boolean;
      tenantId?: string;
    }) => [...queryKeys.campaigns.sections(), params] as const,
    detail: (id: string) => [...queryKeys.campaigns.all, 'detail', id] as const,
    lookup: () => [...queryKeys.campaigns.all, 'lookup'] as const,
  },
  industry: {
    all: ['industry'] as const,
    // Full Level-1 → Level-2 tree (audience/lookups/industry?nested=true).
    tree: () => [...queryKeys.industry.all, 'tree'] as const,
  },
  follows: {
    all: ['follows'] as const,
    followers: (scope: string, objectId: string) =>
      [...queryKeys.follows.all, 'followers', scope, objectId] as const,
    suggestions: (scope: string, objectId: string, q: string) =>
      [...queryKeys.follows.all, 'suggestions', scope, objectId, q] as const,
  },
  clientConfig: {
    all: ['clientConfig'] as const,
    byCodes: (codes: readonly string[]) => [...queryKeys.clientConfig.all, ...codes] as const,
  },
  tenants: {
    all: ['tenants'] as const,
    list: () => [...queryKeys.tenants.all, 'list'] as const,
    followers: (tenantId: string) => [...queryKeys.tenants.all, 'followers', tenantId] as const,
  },
  dsp: {
    all: ['dsp'] as const,
    associations: () => [...queryKeys.dsp.all, 'association'] as const,
    association: (audienceId: string) => [...queryKeys.dsp.associations(), audienceId] as const,
    campaignSearches: () => [...queryKeys.dsp.all, 'campaigns'] as const,
    campaignSearch: (source: string, q: string) =>
      [...queryKeys.dsp.campaignSearches(), source, q] as const,
  },
  exploreExportJob: {
    all: ['exploreExportJob'] as const,
    detail: (jobId: string) => [...queryKeys.exploreExportJob.all, jobId] as const,
  },
  onboarding: {
    all: ['onboarding'] as const,
    /**
     * Where the flow says this account belongs. One per session, not per step.
     * Keyed on the pinned ICP (NPJB-726), so editing one ICP never overwrites
     * the cached state of the current one. No id means the current ICP.
     */
    state: (icpId?: string) => [...queryKeys.onboarding.all, 'state', icpId ?? 'current'] as const,
    /**
     * Whether the tenant holds an ICP, for the Intelligence setup block (NPJB-777). Its own
     * entry, keyed on the account read: an internal user switching client must never see the
     * previous client's answer, and the flow's writes to `state` must not land here.
     */
    tenantState: (tenantId?: string) =>
      [...queryKeys.onboarding.all, 'tenantState', tenantId ?? 'own'] as const,
    siteReads: (icpId?: string) =>
      [...queryKeys.onboarding.all, 'siteRead', icpId ?? 'current'] as const,
    /**
     * Keyed on the read's id rather than the domain, so steps 3 and 4 share one
     * cache entry and one poll between them.
     */
    siteRead: (siteReadId: string) =>
      [...queryKeys.onboarding.all, 'siteReadPoll', siteReadId] as const,
    /** Step 6 (NPJB-757): every audience the caller can see, to find this ICP's latest. */
    previewAudiences: () => [...queryKeys.onboarding.all, 'previewAudiences'] as const,
    /** Step 6: the audience's #1-ranked account. */
    previewAccount: (audienceId: string) =>
      [...queryKeys.onboarding.all, 'previewAccount', audienceId] as const,
    topics: (icpId?: string) =>
      [...queryKeys.onboarding.all, 'topics', icpId ?? 'current'] as const,
    topicSearch: (query: string) => [...queryKeys.onboarding.all, 'topicSearch', query] as const,
    technographics: (icpId?: string) =>
      [...queryKeys.onboarding.all, 'technographics', icpId ?? 'current'] as const,
  },
  /** The buying committee. Its own namespace, not a profile's: both profile pages read one
   *  account's entry, and the audience page reads the whole audience (`domHash` null).
   *  Keying it under either profile would have given one account as many copies as it has
   *  contacts. */
  committee: {
    all: ['committee'] as const,
    page: (
      audienceId: string,
      domHash: string | null,
      movedOnly: boolean,
      page: { limit: number; offset: number },
    ) => [...queryKeys.committee.all, audienceId, domHash, movedOnly, page] as const,
    /** NPJB-716. The whole audience's committee by function and level. */
    breakdown: (audienceId: string, movedOnly: boolean) =>
      [...queryKeys.committee.all, 'breakdown', audienceId, movedOnly] as const,
  },
  /** NPJB-714. One namespace for both journeys. A person's journey is keyed on their
   *  person key; the account's carries `null` there. The picked origins are part of the
   *  key, sorted, so the same filter picked in any order shares one entry. */
  journey: {
    all: ['journey'] as const,
    list: (audienceId: string, domHash: string, personKey: string | null, origins: string[]) =>
      [
        ...queryKeys.journey.all,
        audienceId,
        domHash,
        personKey,
        [...origins].sort((a, b) => a.localeCompare(b)),
      ] as const,
  },
  accountProfile: {
    all: ['accountProfile'] as const,
    /** Keyed on audience + account: the play is computed against the audience, so the same
     *  company under a different audience is a different answer. */
    account: (audienceId: string, domHash: string) =>
      [...queryKeys.accountProfile.all, 'account', audienceId, domHash] as const,
    /** Audience + account: the backend ranks the ten against the audience's topics,
     *  so the same account under a different audience is a different answer. */
    signals: (audienceId: string, domHash: string) =>
      [...queryKeys.accountProfile.all, 'signals', audienceId, domHash] as const,
    /** Audience + account: the rationale argues for the play, and the
     *  play is computed against the audience's filters. */
    rationale: (audienceId: string, domHash: string) =>
      [...queryKeys.accountProfile.all, 'rationale', audienceId, domHash] as const,
    /** NPJB-757 C6. Audience + account: the route checks the audience's tenant scope. */
    techStack: (audienceId: string, domHash: string) =>
      [...queryKeys.accountProfile.all, 'techStack', audienceId, domHash] as const,
  },
  /** NPJB-586. The Intelligence audience page's Overview reads. */
  intelligenceOverview: {
    all: ['intelligenceOverview'] as const,
    /** The moved filter narrows the answer, so it is part of the key. */
    figures: (audienceId: string, movedOnly: boolean) =>
      [...queryKeys.intelligenceOverview.all, 'figures', audienceId, movedOnly] as const,
    plays: (audienceId: string, movedOnly: boolean) =>
      [...queryKeys.intelligenceOverview.all, 'plays', audienceId, movedOnly] as const,
    /** The rows asked for are the question: a different listing is a different read. */
    rationales: (audienceId: string, domHashes: string[]) =>
      [...queryKeys.intelligenceOverview.all, 'rationales', audienceId, ...domHashes] as const,
    /** NPJB-715. The Accounts tab's breakdowns, for one narrowing (moved filter, play). */
    breakdown: (audienceId: string, narrowing: string) =>
      [...queryKeys.intelligenceOverview.all, 'breakdown', audienceId, narrowing] as const,
    /** NPJB-715. One page of the Accounts tab's table, for one narrowing and order. */
    accountsPage: (audienceId: string, view: string, offset: number, limit: number) =>
      [...queryKeys.intelligenceOverview.all, 'accounts', audienceId, view, offset, limit] as const,
  },
  contactProfile: {
    all: ['contactProfile'] as const,
    /** Keyed on the full identity triple — the same person under a different audience is
     *  a different play, so it must not share a cache entry. */
    contact: (audienceId: string, domHash: string, personKey: string) =>
      [...queryKeys.contactProfile.all, 'contact', audienceId, domHash, personKey] as const,
    signals: (domHash: string, personKey: string) =>
      [...queryKeys.contactProfile.all, 'signals', domHash, personKey] as const,
  },
  /** The Intelligence audience's Export modal (NPJB-intelligence-export). */
  intelligenceExport: {
    all: ['intelligenceExport'] as const,
    // updatedAt is in the key so editing the audience's filters busts the cached preview —
    // without it the modal showed the pre-edit counts next to post-edit dashboard figures.
    preview: (icpId: string, updatedAt: string) =>
      [...queryKeys.intelligenceExport.all, 'preview', icpId, updatedAt] as const,
  },
};
